import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { RolodexEntryType } from 'types'

interface UsePointCloudDataParams {
  siteID?: string
  pointCloudIDs?: string[]
}

export const usePointCloudData = ({
  siteID,
  pointCloudIDs,
}: UsePointCloudDataParams) => {
  const { sites, organizations } = useRolodexEntries(
    !siteID
      ? undefined
      : {
          links: [RolodexEntryType.ORGANIZATION],
          type: [RolodexEntryType.SITE],
          ids: [siteID],
        }
  )

  const { pointCloudScenes } = useRolodexEntries({
    type: [RolodexEntryType.POINT_CLOUD_SCENE],
    tags: siteID ? { site: [siteID] } : undefined,
  })

  // Check if any of the provided pointCloudIDs is actually a scene ID
  const sceneFromPointCloudIDs =
    pointCloudIDs?.length === 1
      ? pointCloudScenes.find((scene) => scene.id === pointCloudIDs[0])
      : undefined

  // If we found a scene, extract all point cloud IDs from it
  const resolvedPointCloudIDs = useMemo(() => {
    if (sceneFromPointCloudIDs) {
      return Array.from(sceneFromPointCloudIDs.data.potree_point_clouds.keys())
    }
    return pointCloudIDs
  }, [sceneFromPointCloudIDs, pointCloudIDs])

  // Find active scene that contains any of our resolved point cloud IDs
  const activePointCloudScene =
    pointCloudScenes.find((scene) =>
      resolvedPointCloudIDs?.some((id) =>
        Array.from(scene.data.potree_point_clouds.keys() ?? []).includes(id)
      )
    ) || sceneFromPointCloudIDs

  const ids = resolvedPointCloudIDs?.length
    ? resolvedPointCloudIDs // Use resolved point cloud IDs
    : activePointCloudScene?.data.potree_point_clouds.size
      ? Array.from(activePointCloudScene.data.potree_point_clouds.keys() ?? [])
      : undefined

  // If only siteID is specified, we're on the homepage and should show all
  // point clouds for the site
  const tags =
    siteID && (!resolvedPointCloudIDs?.length || !activePointCloudScene)
      ? { site: [siteID] }
      : undefined

  const { potreePointClouds, isLoading: isLoadingPotreePointClouds } =
    useRolodexEntries({
      type: [RolodexEntryType.POTREE_POINT_CLOUD],
      ids,
      tags,
    })

  const activePointCloud = useMemo(() => {
    return resolvedPointCloudIDs?.length
      ? potreePointClouds.find((pc) => pc.id === resolvedPointCloudIDs[0])
      : potreePointClouds[0]
  }, [potreePointClouds, resolvedPointCloudIDs])

  return {
    // Site data
    sites,
    organizations,
    site: sites.at(0),
    organization: organizations.at(0),

    // Point cloud data
    pointCloudScenes,
    sceneFromPointCloudIDs,
    resolvedPointCloudIDs,
    activePointCloudScene,
    potreePointClouds,
    activePointCloud,
    isLoadingPotreePointClouds,
  }
}
